'use client';

import {
  faEarthAmerica,
  faGraduationCap,
  faNoteMedical,
} from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { ReactNode } from 'react';
import {
  EntityRatingCommentCount,
  Professional,
  Rating,
} from '@medprev-web-app/api-graphql-client';

import { ProfessionalAbout } from './professional-about.component';
import { ProfessionalServiceLocations } from './professional-service-locations.component';

import { EntitySection } from '@/app/(main)/(pages)/_components/entities/entity-section.component';
import { EntitySectionDivider } from '@/app/(main)/(pages)/_components/entities/entity-section-divider.component';
import {
  EntityTab,
  EntityTabContent,
  EntityTabList,
  EntityTabProvider,
  EntityTabTrigger,
} from '@/app/(main)/(pages)/_components/entities/entity-tab.component';
import { RatingCard } from '@/app/(main)/(pages)/_components/entities/entity-rating-card.component';
import { ItemList } from '@/app/(main)/(pages)/_components/entities/item-list.component';
import { RatingComment } from '@/types';
import {
  EntityLoadMoreComments,
  LoadCommentsPage,
} from '@/app/(main)/(pages)/_components/entities/entity-load-more-comments.component';
import { CommentLoading } from '@/app/(main)/(pages)/_components/entities/entity-comment-loading.component';
import { PROFESSIONAL_PROFILE_COMMENTS_PAGE_SIZE } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/_services/get-professional-profile-from-search-api.service';
import {
  formatProfessionalFormations,
  formatProfessionalLanguages,
  formatProfessionalTreatments,
} from '@/utils/professional/online-professional.utils';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { InPersonScheduleSource } from '@/utils/professional/extract-professional-location';
import { PROFESSIONAL_FORMATIONS_SECTION_ID } from '@/utils/professional/main-post-graduation.util';

interface TabProfessionalProps {
  professional: Professional;
  countComments?: EntityRatingCommentCount | null;
  inPersonSchedules: InPersonScheduleSource[];
  hasTelehealth: boolean;
  initialComments: RatingComment[];
  hasMoreComments?: boolean;
  loadCommentsPage?: LoadCommentsPage;
  commentsSort?: ReactNode;
  commentsKey?: string;
  isSortingComments?: boolean;
  schedulesSection?: ReactNode;
}

export function TabProfessional({
  professional,
  countComments,
  inPersonSchedules,
  hasTelehealth,
  initialComments,
  hasMoreComments,
  loadCommentsPage,
  commentsSort,
  commentsKey,
  isSortingComments,
  schedulesSection,
}: TabProfessionalProps) {
  const formattedFormations = formatProfessionalFormations(professional);

  const formattedLanguages = formatProfessionalLanguages(professional);

  const formattedTreatments = formatProfessionalTreatments(professional);

  const hasServiceLocations = hasTelehealth || inPersonSchedules.length > 0;

  const hasComments = Boolean(countComments) && Number(countComments?.total) > 0;

  const hasAbout = Boolean(professional.description);
  const hasFormations = formattedFormations.length > 0;
  const hasTreatments = formattedTreatments.length > 0;
  const hasLanguages = formattedLanguages.length > 0;

  const hasAboutSections = hasServiceLocations || hasAbout || hasFormations || hasTreatments || hasLanguages;

  return (
    <>
      <EntityTabProvider>
        <EntityTab
          defaultValue="about"
          tabs={['about', 'rating']}
        >
          <EntityTabList className="sticky top-0 bg-white pt-2 pb-4">
            <EntityTabTrigger
              value="about"
              data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_ABOUT_TAB}
            >
              Sobre
            </EntityTabTrigger>
            <EntityTabTrigger
              value="rating"
              data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_REVIEWS_TAB}
            >
              Avaliações
            </EntityTabTrigger>
          </EntityTabList>

          <div data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_TABS_CONTAINER}>
            <EntityTabContent value="about">
              {hasServiceLocations && (
                <>
                  <ProfessionalServiceLocations
                    inPersonSchedules={inPersonSchedules}
                    hasTelehealth={hasTelehealth}
                  />
                </>
              )}

              {hasAbout && (
                <>
                  {hasServiceLocations && <EntitySectionDivider />}

                  <EntitySection
                    title="Sobre mim"
                    id="about-me"
                    className="my-0 py-4"
                    titleStyles="leading-6"
                    removeHr
                  >
                    <ProfessionalAbout professional={professional} />
                  </EntitySection>
                </>
              )}

              {hasFormations && (
                <>
                  {(hasServiceLocations || hasAbout) && <EntitySectionDivider />}

                  <EntitySection
                    icon={faGraduationCap}
                    title="Formação & experiência"
                    id={PROFESSIONAL_FORMATIONS_SECTION_ID}
                    classNameIcon="sm:mt-0.5"
                    className="my-0 py-4 scroll-mt-20"
                    titleStyles="leading-6"
                    removeHr
                  >
                    <ItemList
                      items={formattedFormations}
                      dataTestID="formations"
                      data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_EDUCATION_AND_EXPERIENCE}
                    />
                  </EntitySection>
                </>
              )}

              {hasTreatments && (
                <>
                  {(hasServiceLocations || hasAbout || hasFormations) && <EntitySectionDivider />}

                  <EntitySection
                    icon={faNoteMedical}
                    title="Doenças, sintomas ou condições"
                    classNameIcon="sm:mt-0.5"
                    className="my-0 py-4"
                    titleStyles="leading-6"
                    removeHr
                  >
                    <ItemList
                      items={formattedTreatments}
                      dataTestID="treatments"
                      data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_DISEASES}
                    />
                  </EntitySection>
                </>
              )}

              {hasLanguages && (
                <>
                  {(hasServiceLocations || hasAbout || hasFormations || hasTreatments) && <EntitySectionDivider />}

                  <EntitySection
                    icon={faEarthAmerica}
                    title={formattedLanguages.length > 1 ? 'Idiomas' : 'Idioma'}
                    classNameIcon="sm:mt-0.5"
                    className="my-0 py-4"
                    titleStyles="leading-6"
                    removeHr
                  >
                    <ItemList
                      items={formattedLanguages}
                      dataTestID="languages"
                      data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_LANGUAGES}
                    />
                  </EntitySection>
                </>
              )}
            </EntityTabContent>

            {schedulesSection && (
              <div className="xl:hidden">
                {hasAboutSections && <EntitySectionDivider />}
                {schedulesSection}
              </div>
            )}

            <EntityTabContent value="rating">
              {hasAboutSections && <EntitySectionDivider />}

              <EntitySection className="my-0" removeHr>
                <RatingCard
                  rating={professional.rating as Rating}
                  countComments={countComments}
                  commentsSort={commentsSort}
                />
              </EntitySection>

              {hasComments && (isSortingComments
                ? (
                    <div aria-busy data-testid={DataTestIdEnum.ENTITY_COMMENTS_LOADING}>
                      {Array.from({ length: PROFESSIONAL_PROFILE_COMMENTS_PAGE_SIZE }).map((_, index) => (
                        <CommentLoading key={index} />
                      ))}
                    </div>
                  )
                : (
                    <EntityLoadMoreComments
                      key={commentsKey}
                      countComments={countComments}
                      professionalId={professional.id as string}
                      initialComments={initialComments}
                      loadPage={loadCommentsPage}
                      hasMore={hasMoreComments}
                      className="mb-0 md:mb-0"
                      commentClassName="first:mt-3"
                      analyticsEventShowMore={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_COMMENT}
                      analyticsEventShowMoreComments={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_SHOW_MORE_COMMENTS}
                    />
                  ))}
            </EntityTabContent>
          </div>
        </EntityTab>
      </EntityTabProvider>
    </>
  );
}
